// src/api/portalPersonalisationService.js
// Portal Personalisation Campaigns — content shown in the Customer Portal to
// specific customer segments for a limited time.
//
// Data model
//   portalPersonalisations/{campaignId}
//     salonId, type ('popup' | 'content'), name, status ('active' | 'cancelled'),
//     content fields (popup: imageUrl/headerText/bodyText/ctaText,
//                     content: heroImage/galleryImages),
//     scheduling fields (schedulingType, scheduledDate/Time, scheduledEndDate/Time, recurring),
//     targetingType, targetingCriteria, audienceAll, audienceCount, createdAt, createdBy
//     popup campaigns also carry maxPerDay / maxPerMonth (per-customer display caps)
//   portalPersonalisations/{campaignId}/impressions/{customerId}
//     salonId, customerId, shownAt: [ms timestamps this month] — written by the customer's own
//     portal each time the popup is shown, so the caps follow the customer across devices.
//   portalPersonalisations/{campaignId}/recipients/{customerId}
//     salonId, customerId — one doc per targeted customer. Only written when the
//     audience is a segment (audienceAll === false). The customer portal checks for
//     its own doc, so a customer can never read another customer's membership.

import {
  collection,
  doc,
  getDoc,
  getDocs,
  query,
  setDoc,
  where,
  writeBatch,
  serverTimestamp
} from 'firebase/firestore';
import { db } from '../utils/firebase';
import { getCustomerTargetingPreview } from './salonApi';

export const PERSONALISATION_COLLECTION = 'portalPersonalisations';
const BATCH_SIZE = 400; // Firestore batches are capped at 500 writes

const DAY_NAMES = ['sunday', 'monday', 'tuesday', 'wednesday', 'thursday', 'friday', 'saturday'];
const MONTH_NAMES = [
  'january', 'february', 'march', 'april', 'may', 'june',
  'july', 'august', 'september', 'october', 'november', 'december'
];

// ─────────────────────────────────────────────────────────────────────────────
// Scheduling window helpers
// ─────────────────────────────────────────────────────────────────────────────

const parseLocal = (dateStr, timeStr) => new Date(`${dateStr}T${timeStr || '00:00'}`);

const startOfDay = (d) => {
  const copy = new Date(d);
  copy.setHours(0, 0, 0, 0);
  return copy;
};

// Does a recurring campaign start a window on the given calendar day?
const occursOn = (recurring, day) => {
  switch (recurring.frequency) {
    case 'weekly':
      return DAY_NAMES[day.getDay()] === recurring.dayOfWeek;
    case 'monthly':
      return day.getDate() === Number(recurring.dayOfMonth);
    case 'annually':
      return MONTH_NAMES[day.getMonth()] === recurring.monthOfYear &&
        day.getDate() === Number(recurring.dayOfYear);
    case 'daily':
    default:
      return true;
  }
};

// 1-based number of the occurrence that falls on `day` (counting from startDate).
const cycleNumber = (recurring, startDate, day) => {
  let count = 0;
  const cursor = new Date(startDate);
  let guard = 0;
  while (cursor <= day && guard < 4000) {
    if (occursOn(recurring, cursor)) count += 1;
    cursor.setDate(cursor.getDate() + 1);
    guard += 1;
  }
  return count;
};

const isRecurringLive = (campaign, now) => {
  const recurring = campaign.recurring;
  if (!recurring || !recurring.startDate || !campaign.scheduledTime || !campaign.scheduledEndTime) {
    return false;
  }

  const [startH, startM] = campaign.scheduledTime.split(':').map(Number);
  const [endH, endM] = campaign.scheduledEndTime.split(':').map(Number);
  let durationMs = ((endH * 60 + endM) - (startH * 60 + startM)) * 60000;
  if (durationMs <= 0) durationMs += 24 * 60 * 60 * 1000; // window runs past midnight

  const firstDay = startOfDay(parseLocal(recurring.startDate));
  const lastDay = recurring.endType === 'date' && recurring.endDate
    ? startOfDay(parseLocal(recurring.endDate))
    : null;

  // A window that began yesterday can still be running after midnight, so check both days.
  for (const offset of [0, -1]) {
    const day = startOfDay(now);
    day.setDate(day.getDate() + offset);

    if (day < firstDay) continue;
    if (lastDay && day > lastDay) continue;
    if (!occursOn(recurring, day)) continue;
    if (recurring.endType === 'cycles' && recurring.numberOfCycles > 0 &&
        cycleNumber(recurring, firstDay, day) > recurring.numberOfCycles) continue;

    const windowStart = new Date(day);
    windowStart.setHours(startH, startM, 0, 0);
    const windowEnd = new Date(windowStart.getTime() + durationMs);
    if (now >= windowStart && now < windowEnd) return true;
  }
  return false;
};

/** True when the campaign's schedule says it is showing at `now`. */
export const isCampaignLive = (campaign, now = new Date()) => {
  if (!campaign || campaign.status === 'cancelled') return false;

  if (campaign.schedulingType === 'recurring') {
    return isRecurringLive(campaign, now);
  }

  if (!campaign.scheduledDate || !campaign.scheduledTime ||
      !campaign.scheduledEndDate || !campaign.scheduledEndTime) {
    return false;
  }
  const start = parseLocal(campaign.scheduledDate, campaign.scheduledTime);
  const end = parseLocal(campaign.scheduledEndDate, campaign.scheduledEndTime);
  return now >= start && now < end;
};

/** 'scheduled' | 'live' | 'ended' | 'cancelled' — used by the admin listing. */
export const getCampaignState = (campaign, now = new Date()) => {
  if (campaign.status === 'cancelled') return 'cancelled';
  if (isCampaignLive(campaign, now)) return 'live';

  if (campaign.schedulingType === 'recurring') {
    const recurring = campaign.recurring || {};
    if (recurring.startDate && startOfDay(now) < startOfDay(parseLocal(recurring.startDate))) {
      return 'scheduled';
    }
    if (recurring.endType === 'date' && recurring.endDate &&
        startOfDay(now) > startOfDay(parseLocal(recurring.endDate))) {
      return 'ended';
    }
    // Between occurrences: cycles-based campaigns can't cheaply be told apart, so call it scheduled.
    return 'scheduled';
  }

  if (campaign.scheduledDate && campaign.scheduledTime &&
      now < parseLocal(campaign.scheduledDate, campaign.scheduledTime)) {
    return 'scheduled';
  }
  return 'ended';
};

// ─────────────────────────────────────────────────────────────────────────────
// Audience resolution (admin side)
// ─────────────────────────────────────────────────────────────────────────────

/**
 * Resolve the customers a targeting selection matches.
 * Reuses the same matcher the other campaign channels use for "Preview Target Customers".
 * Returns { audienceAll, customers: [{id, ...}], totalCount }.
 */
export const resolveAudience = async (salonId, targetingType, targetingCriteria) => {
  const preview = await getCustomerTargetingPreview(salonId, { targetingType, targetingCriteria });
  let customers = preview.eligibleCustomers;

  // Location filter for transaction-based targeting (same rule the send flows apply)
  const locationId = targetingCriteria?.transactionLocationId;
  if (targetingType === 'customer_transaction' && locationId && locationId !== 'not_selected') {
    customers = customers.filter(c => c.locationId === locationId);
  }

  return {
    audienceAll: targetingType === 'all_customers',
    customers,
    totalCount: preview.totalCount
  };
};

// ─────────────────────────────────────────────────────────────────────────────
// Create / list / cancel (admin side)
// ─────────────────────────────────────────────────────────────────────────────

const deleteRecipientRefs = async (refs) => {
  for (let i = 0; i < refs.length; i += BATCH_SIZE) {
    const batch = writeBatch(db);
    refs.slice(i, i + BATCH_SIZE).forEach(ref => batch.delete(ref));
    await batch.commit();
  }
};

// Lists the audience through the parent campaign, so the campaign must still exist.
const deleteRecipients = async (campaignId) => {
  const snap = await getDocs(collection(db, PERSONALISATION_COLLECTION, campaignId, 'recipients'));
  await deleteRecipientRefs(snap.docs.map(d => d.ref));
};

/**
 * Save a campaign. Recipients are written first and the campaign document last, so the
 * customer portal never sees a campaign whose audience list is still incomplete.
 */
export const createPersonalisationCampaign = async ({ campaign, recipients }) => {
  const campaignRef = doc(collection(db, PERSONALISATION_COLLECTION));
  const written = []; // recipient refs committed so far, for cleanup if a later step fails

  try {
    for (let i = 0; i < recipients.length; i += BATCH_SIZE) {
      const batch = writeBatch(db);
      const refs = recipients.slice(i, i + BATCH_SIZE).map(customer => {
        const recipientRef = doc(db, PERSONALISATION_COLLECTION, campaignRef.id, 'recipients', customer.id);
        batch.set(recipientRef, { salonId: campaign.salonId, customerId: customer.id });
        return recipientRef;
      });
      await batch.commit();
      written.push(...refs);
    }

    await setDoc(campaignRef, {
      ...campaign,
      status: 'active',
      createdAt: serverTimestamp()
    });
  } catch (err) {
    // Don't leave orphaned recipient docs behind
    await deleteRecipientRefs(written).catch(() => {});
    throw err;
  }

  return campaignRef.id;
};

export const listPersonalisationCampaigns = async (salonId) => {
  const snap = await getDocs(
    query(collection(db, PERSONALISATION_COLLECTION), where('salonId', '==', salonId))
  );
  return snap.docs
    .map(d => ({ id: d.id, ...d.data() }))
    .sort((a, b) => (b.createdAt?.seconds || 0) - (a.createdAt?.seconds || 0));
};

export const deletePersonalisationCampaign = async (campaignId) => {
  await deleteRecipients(campaignId);
  // Display counts are only bookkeeping, so a failure to clear them must never block deleting the campaign
  try {
    const impressions = await getDocs(collection(db, PERSONALISATION_COLLECTION, campaignId, 'impressions'));
    await deleteRecipientRefs(impressions.docs.map(d => d.ref));
  } catch (err) {
    console.warn('Could not clear popup display counts for campaign', campaignId, err);
  }
  const batch = writeBatch(db);
  batch.delete(doc(db, PERSONALISATION_COLLECTION, campaignId));
  await batch.commit();
};

// ─────────────────────────────────────────────────────────────────────────────
// Customer portal
// ─────────────────────────────────────────────────────────────────────────────

/**
 * What personalised content (if any) should this customer see right now?
 * Returns { popup, heroImage, galleryImages } — each null/empty when nothing applies,
 * in which case the portal falls back to the salon's normal Images-page content.
 * If several live campaigns match, the most recently created one wins.
 */
export const getPersonalisationForCustomer = async (salonId, customerId) => {
  const result = { popup: null, heroImage: null, galleryImages: [] };
  if (!salonId || !customerId) return result;

  const snap = await getDocs(
    query(collection(db, PERSONALISATION_COLLECTION), where('salonId', '==', salonId))
  );
  const now = new Date();

  const live = snap.docs
    .map(d => ({ id: d.id, ...d.data() }))
    .filter(c => isCampaignLive(c, now))
    .sort((a, b) => (b.createdAt?.seconds || 0) - (a.createdAt?.seconds || 0));

  const eligible = [];
  for (const campaign of live) {
    if (campaign.audienceAll) {
      eligible.push(campaign);
      continue;
    }
    const recipient = await getDoc(
      doc(db, PERSONALISATION_COLLECTION, campaign.id, 'recipients', customerId)
    );
    if (recipient.exists()) eligible.push(campaign);
  }

  result.popup = eligible.find(c => c.type === 'popup') || null;
  const heroCampaign = eligible.find(c => c.type === 'content' && c.heroImage);
  const galleryCampaign = eligible.find(c => c.type === 'content' && c.galleryImages?.length > 0);
  result.heroImage = heroCampaign ? heroCampaign.heroImage : null;
  result.galleryImages = galleryCampaign ? galleryCampaign.galleryImages : [];
  return result;
};

const localShownKey = (popup, customerId) => `portalPopupShown_${popup.id}_${customerId}`;

const readLocalShown = (popup, customerId) => {
  try {
    const parsed = JSON.parse(localStorage.getItem(localShownKey(popup, customerId)) || '[]');
    return Array.isArray(parsed) ? parsed : [];
  } catch (e) {
    return [];
  }
};

const writeLocalShown = (popup, customerId, shownAt) => {
  try { localStorage.setItem(localShownKey(popup, customerId), JSON.stringify(shownAt)); } catch (e) { /* storage unavailable */ }
};

/**
 * Decide whether the popup may be shown to this customer now and, if so, record the display.
 * Every visit to the home page is a chance to show it, until the caps are reached. Caps are per
 * customer, counted on the customer's local calendar day / month. Counts are kept in Firestore so
 * they follow the customer across devices; if that isn't available they are kept in this browser
 * instead. Campaigns saved before the caps existed default to once per day with no monthly limit.
 * Returns true when the popup should be shown.
 */
export const claimPopupImpression = async (popup, customerId) => {
  const maxPerDay = popup.maxPerDay || 1;
  const maxPerMonth = popup.maxPerMonth || Infinity;

  const impressionRef = doc(db, PERSONALISATION_COLLECTION, popup.id, 'impressions', customerId);
  let shared = true;
  let previous = [];
  try {
    const snap = await getDoc(impressionRef);
    previous = snap.exists() ? snap.data().shownAt || [] : [];
  } catch (err) {
    console.warn('Popup display counts not available in Firestore, counting in this browser instead:', err);
    shared = false;
    previous = readLocalShown(popup, customerId);
  }

  const now = new Date();
  const monthStart = new Date(now.getFullYear(), now.getMonth(), 1).getTime();
  const dayStart = startOfDay(now).getTime();

  // Older months no longer matter, so don't carry them forward
  const shownThisMonth = previous.filter(t => t >= monthStart);
  const shownToday = shownThisMonth.filter(t => t >= dayStart);

  if (shownToday.length >= maxPerDay || shownThisMonth.length >= maxPerMonth) return false;

  const updated = [...shownThisMonth, now.getTime()];
  if (shared) {
    try {
      await setDoc(impressionRef, { salonId: popup.salonId, customerId, shownAt: updated });
    } catch (err) {
      console.warn('Could not save popup display count to Firestore, counting in this browser instead:', err);
      writeLocalShown(popup, customerId, updated);
    }
  } else {
    writeLocalShown(popup, customerId, updated);
  }
  return true;
};

/**
 * Who a campaign targets, as [{ id, displayName, email }].
 * "All customers" campaigns have no stored list, so this is every current customer of the salon.
 * Segment campaigns return the audience saved when the campaign was created.
 */
export const getCampaignRecipients = async (campaign) => {
  const customersSnap = await getDocs(
    query(collection(db, 'customers'), where('salonId', '==', campaign.salonId))
  );
  const customers = customersSnap.docs.map(d => ({
    id: d.id,
    displayName: d.data().displayName || d.data().name || '',
    email: d.data().email || ''
  }));

  if (campaign.audienceAll) return customers;

  const recipientsSnap = await getDocs(
    collection(db, PERSONALISATION_COLLECTION, campaign.id, 'recipients')
  );
  const byId = new Map(customers.map(c => [c.id, c]));
  // Fall back to the id ({email}_{salonId}) if the customer record has since been removed
  return recipientsSnap.docs.map(d =>
    byId.get(d.id) || { id: d.id, displayName: '', email: d.id.split('_')[0] }
  );
};
